import {
  Alert,
  Box,
  ButtonIcon,
  CheckboxStandalone,
  Divider,
  Heading,
  IconShare,
  Inline,
  Stack,
  Text,
  useToast,
} from 'braid-design-system';
import { useCallback, useId, useMemo, useState } from 'react';
import { useQueryParam } from 'use-query-params';

import { useChartjsReady } from '../../../../hooks/useChartjsReady.tsx';
import { IntegrationHealthSummarySection } from '../IntegrationHealthSummarySection/IntegrationHealthSummarySection.tsx';

import { AddFilterButton } from './AddFilterButton.tsx';
import { ControlBox } from './ControlBox.tsx';
import { FilterDisplay } from './FilterDisplay.tsx';
import { FilterModal } from './FilterModal.tsx';
import { IntegrationHealthGroupBy } from './IntegrationHealthGroupBy.tsx';
import { IntegrationHealthRequestsGraph } from './IntegrationHealthRequestsGraph.tsx';
import { RangePicker, rangeToBounds } from './RangePicker.tsx';
import { RequestDetailsTable } from './RequestDetailsTable.tsx';
import { filterTypesMap, specialConfigs } from './config.ts';
import {
  FiltersParam,
  FrozenBoundsParam,
  GroupByParam,
  RangeParam,
} from './queryParams.ts';
import type { FilterData } from './types.ts';
import { useCurrentTimeToTheMinute } from './useCurrentTimeToTheMinute.ts';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import type { Breadcrumb } from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import { ErrorBoundary } from '#src/components/ErrorBoundary/ErrorBoundary.tsx';

export const IntegrationHealthPage = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const showToast = useToast();
  const ready = useChartjsReady();
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [editFilter, setEditFilter] = useState<FilterData | undefined>(
    undefined,
  );

  const [range, setRange] = useQueryParam('range', RangeParam, {
    removeDefaultsFromUrl: true,
  });
  const [groupBy, setGroupBy] = useQueryParam('groupBy', GroupByParam, {
    removeDefaultsFromUrl: true,
  });
  const [rawFilters, setFilters] = useQueryParam('filters', FiltersParam, {
    removeDefaultsFromUrl: true,
  });
  const [frozenBounds, setFrozenBounds] = useQueryParam(
    'freeze',
    FrozenBoundsParam,
    { removeDefaultsFromUrl: true },
  );

  const filters = useMemo(() => {
    const config = groupBy === 'None' ? undefined : filterTypesMap.get(groupBy);

    if (!config) {
      return rawFilters;
    }

    return (config.requireFilter ?? []).reduceRight<FilterData[]>(
      (currentFilters, requireFilter) => {
        const requireConfig = specialConfigs[requireFilter];

        const alreadyHas = currentFilters.some(
          (filter) =>
            filter.type === requireConfig.type &&
            filter.value === requireConfig.value &&
            filter.comparator === (requireConfig.comparator ?? 'Equal'),
        );

        if (!alreadyHas) {
          return [
            {
              id: requireFilter,
              type: requireConfig.type,
              comparator: requireConfig.comparator ?? 'Equal',
              value: requireConfig.value,
              groupBy: config.label,
              label: requireConfig.label,
            },
            ...currentFilters,
          ];
        }

        return currentFilters;
      },
      rawFilters,
    );
  }, [groupBy, rawFilters]);

  const time = useCurrentTimeToTheMinute();

  const onChangeBounds = useCallback(
    (newBounds: [Date, Date]) => {
      setRange({ option: 'custom', bounds: newBounds });
      setFrozenBounds(undefined);
    },
    [setRange, setFrozenBounds],
  );

  const bounds = useMemo(
    () => frozenBounds ?? rangeToBounds(range, time),
    [frozenBounds, range, time],
  );

  const toggleId = useId();

  return (
    <BasePage title="Your integration health" breadcrumbs={breadcrumbs}>
      <Text>
        The integration health tool offers insights into your integration’s
        traffic, health and error rates. You can explore requests, diagnose any
        issues with your integration, and review the overall health of your
        integration.
      </Text>

      {showFilterModal ? (
        <FilterModal
          onClose={() => {
            setShowFilterModal(false);
            setEditFilter(undefined);
          }}
          filter={editFilter}
          onAddFilter={(filter) =>
            setFilters((currentFilters) =>
              filter.replacesId
                ? currentFilters.map((f) =>
                    f.id === filter.replacesId ? filter : f,
                  )
                : [...currentFilters, filter],
            )
          }
          onRemoveFilter={(filter) =>
            setFilters((currentFilters) =>
              currentFilters.filter((f) => f.id !== filter.id),
            )
          }
        />
      ) : null}

      <Stack space="xlarge">
        <ErrorBoundary
          fallback={
            <Alert tone="caution">
              <Text>
                Unable to render the Integration Health Summary. Please try
                again later.
              </Text>
            </Alert>
          }
        >
          <IntegrationHealthSummarySection />
        </ErrorBoundary>

        <Divider />
        <ErrorBoundary
          fallback={
            <Alert tone="caution">
              <Text>
                Unable to render the Requests Graph. Please try again later.
              </Text>
            </Alert>
          }
        >
          <Stack space="small">
            <Box
              display="flex"
              gap="medium"
              justifyContent="spaceBetween"
              flexWrap="wrap"
            >
              <Heading level="4">Requests</Heading>

              <Box display="flex" gap="small" style={{ alignItems: 'stretch' }}>
                <ControlBox>
                  <Text weight="strong">
                    <RangePicker
                      setRange={(newRange) => {
                        setRange(newRange);

                        if (frozenBounds) {
                          setFrozenBounds(rangeToBounds(newRange, time));
                        }
                      }}
                      range={range}
                    />
                  </Text>
                </ControlBox>

                <ControlBox>
                  <Inline space="xsmall" alignY="center">
                    <CheckboxStandalone
                      id={toggleId}
                      aria-labelledby={`${toggleId}Label`}
                      disabled={range.option === 'custom'}
                      checked={frozenBounds === undefined}
                      onChange={(e) =>
                        setFrozenBounds(
                          e.currentTarget.checked ? undefined : bounds,
                        )
                      }
                      size="small"
                    />
                    <Box
                      component="label"
                      htmlFor={toggleId}
                      id={`${toggleId}Label`}
                    >
                      <Text
                        weight="strong"
                        tone={
                          range.option === 'custom' ? 'secondary' : 'neutral'
                        }
                      >
                        Auto-refresh
                      </Text>
                    </Box>
                  </Inline>
                </ControlBox>

                <Box display="flex" alignItems="center">
                  <ButtonIcon
                    id={useId()}
                    label="Share a link with these filters"
                    icon={<IconShare />}
                    onClick={async () => {
                      await navigator.clipboard.writeText(window.location.href);

                      showToast({
                        message: 'Sharing link copied to clipboard',
                        tone: 'positive',
                      });
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <IntegrationHealthGroupBy
              groupBy={groupBy}
              setGroupBy={setGroupBy}
            />

            <Box
              display="flex"
              flexWrap="wrap"
              gap="small"
              style={{ alignItems: 'stretch' }}
            >
              <AddFilterButton onClick={() => setShowFilterModal(true)} />

              {filters.length > 0 ? (
                <Box
                  style={{ width: 1 }}
                  boxShadow="borderNeutralLight"
                  marginY="xxsmall"
                />
              ) : null}

              {filters.map((filter) => (
                <FilterDisplay
                  filter={filter}
                  key={filter.id}
                  edit={() => {
                    setEditFilter(filter);
                    setShowFilterModal(true);
                  }}
                  remove={() =>
                    setFilters((currentFilters) =>
                      currentFilters.filter(
                        (currentFilter) => currentFilter.id !== filter.id,
                      ),
                    )
                  }
                />
              ))}
            </Box>

            <Stack space="none">
              <IntegrationHealthRequestsGraph
                key={ready ? 'loaded' : 'placeholder'}
                onChangeBounds={onChangeBounds}
                filters={filters}
                bounds={bounds}
                skipLoading={!ready}
                groupBy={groupBy}
                setGroupBy={setGroupBy}
                onAddFilter={(filter) =>
                  setFilters((currentFilters) => [...currentFilters, filter])
                }
              />
            </Stack>
          </Stack>
        </ErrorBoundary>
        <ErrorBoundary
          fallback={
            <Alert tone="caution">
              <Text>
                Unable to render the Request Details table. Please try again
                later.
              </Text>
            </Alert>
          }
        >
          <RequestDetailsTable bounds={bounds} filters={filters} />
        </ErrorBoundary>
      </Stack>
    </BasePage>
  );
};
